/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Boris Kudlač (kudlac@cadwork.cz)
 *
 */

import RenderDocument from "App/render_document";
import {mat3, mat4, quat, vec2, vec3} from "gl-matrix";
import {logInfo} from "App/debug";
import BoundingBox from "App/utils/bounding_box";
import CameraAnimator from "App/animations/camera_animator";
import Factory from "App/animations/interpolators/factory";
import Quaternion from "App/animations/interpolators/quaternion";
import View from "App/view/view";
import ViewControls from "App/view/controls/view_controls";
import Canvas from "App/canvas";

import IScene from "js_interface/src/scene_interface";
import {EViewControls} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

/**
 *  ViewMode encapsulates creation and managing of the views.
 *
 * @param {Canvas} aCanvas
 * @constructor
 */
export default
class ViewMode {

    private static UIDGenerator: number = 0;

    protected _UID: number = ViewMode.UIDGenerator++;

    protected sceneAPI: IScene;
    protected canvas: any;
    protected rdocument: RenderDocument;

    protected views: View[] = [];
    protected activeView: View = null; // view in which a mouse click happened (see the mousemove handling for details)

    protected _pgc: any = {
        camPos: vec3.create(),
        camTarget: vec3.create()
    };

    private _redrawScheduled = true;

    private _deviceOrientation: any = {
        changed: false,
        tiltLRDeg: 0.0,
        tiltFBDeg: 0.0,
        directionDeg: 0.0
    };

    constructor(aRenderDocument: RenderDocument) {
        this.sceneAPI = aRenderDocument.getSceneAPI();
        this.canvas = this.sceneAPI.getCanvas();
        this.rdocument = aRenderDocument;
        this.rdocument.registerObserver(this);
    }

    public static getModeType(): string {
        return "";
    }

    /**
     * Static method (not in prototype) as it does not use any member from the ViewMode instance
     * @param aViewMatrix
     * @returns {quat}
     */
    public static computeQuaternion(aViewMatrix: mat4) {
        let vm = aViewMatrix;
        let rotVm = mat3.create();

        mat3.fromMat4(rotVm, vm);

        let myquat = quat.create();
        quat.fromMat3(myquat, rotVm);

        return myquat;
    }

    public getCanvas(): Canvas {
        return this.canvas;
    }

    public getUID(): number {
        return this._UID;
    }

    public addView(aView: View) {
        this.views.push(aView);
        aView.setIndexInViewMode(this.views.length - 1);
    }

    public getViews(): View[] {
        return this.views;
    }

    public getViewWithIndex(aIndex: number): View {
        for (let v in this.views) {
            let view = this.views[v];
            if (view.getIndexInViewMode() === aIndex) {
                return view;
            }
        }

        return null;
    }

    public getActiveView(): View {
        return this.activeView;
    }

    public createLights() {
        for (let v in this.views) {
            let view = this.views[v];
            view.clearLights();
            view.addLight(this.rdocument.getHeadLight());
            view.addLight(this.rdocument.getSunLight());
        }
    }

    public tick() {
        for (let v in this.views) {
            let view = this.views[v];
            view.tick();
        }
    }

    public render() {
        if (!this._redrawScheduled) {
            return;
        }

        this._redrawScheduled = false;

        for (let v in this.views) {
            let view = this.views[v];
            view.render();
        }
    }

    public scheduleResize(aContentSize: vec2) {
        for (let v in this.views) {
            let view = this.views[v];
            view.scheduleResize(aContentSize);
        }
    }

    public scheduleRedraw() {
        this._redrawScheduled = true;
    }

    public viewpointChanged() {
        for (let v in this.views) {
            let view = this.views[v];
            view.viewpointChanged();
        }
    }

    public setViewControls(aViewControlsType: EViewControls, aSourceViewControls: ViewControls) {
        if (aViewControlsType === null) {
            logInfo("Error: View controls type is null.");
        }

        for (let v in this.views) {
            let view = this.views[v];
            view.setViewControls(aViewControlsType, aSourceViewControls);
        }

        if (this.sceneAPI.viewControlsModule) {
            this.sceneAPI.viewControlsModule.attachViewControls(this.getActiveView().getViewControls());
        }

        this.scheduleRedraw();
    }

    public initLogarithmicDepthBuffer() {
        for (let v in this.views) {
            let view = this.views[v];
            view.initLogarithmicDepthBuffer();
        }

        this.scheduleRedraw();
    }

    public getViewControls(): ViewControls {
        return this.activeView.getViewControls();
    }

    public setUsePointerLock() {
        this.activeView.setUsePointerLock();
    }

    public onBoundingBoxUpdated(aBoundingBox: BoundingBox, aBoundingBoxForNPC: BoundingBox) {
        for (let view of this.views) {
            view.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);
        }
    }

    public animateView(aPosition: vec3, aTarget: vec3, aFov: number, aAnimationTime: number, aAnimationFinishCB) {
        let cameraAnimator = new CameraAnimator(this.sceneAPI);
        cameraAnimator.setInterpolator(Factory.create(Quaternion.TypeName));
        cameraAnimator.setFps(1.0);
        cameraAnimator.setAnimationFinishCB(aAnimationFinishCB);
        cameraAnimator.preventTexturesSwitching(true);

        let camera = this.activeView.getCamera();

        camera.setFieldOfView(aFov);

        let startQuat = ViewMode.computeQuaternion(camera.getViewMatrix());
        let endQuat = ViewMode.computeQuaternion(camera.getCustomViewMatrix(aPosition, aTarget));

        cameraAnimator.setAnimationFrames([
            {
                position: vec3.clone(camera.getCameraPosition()),
                target: vec3.clone(camera.getCameraTarget()),
                quat: startQuat
            },
            {
                position: aPosition,
                target: aTarget,
                quat: endQuat
            }
        ]);

        this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
    }

    public animateAllViews(aPosition: vec3, aTarget: vec3, aFov: number, aAnimationTime: number, aAnimationFinishCB) {
        for (let view of this.views) {
            let cameraAnimator = new CameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(Factory.create(Quaternion.TypeName));
            cameraAnimator.setFps(1.0);
            cameraAnimator.setAnimationFinishCB(aAnimationFinishCB);
            cameraAnimator.preventTexturesSwitching(true);

            let camera = view.getCamera();

            camera.setFieldOfView(aFov);

            let startQuat = ViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = ViewMode.computeQuaternion(camera.getCustomViewMatrix(aPosition, aTarget));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: aPosition,
                    target: aTarget,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    public onMousePress(x: number, y: number, buttonState: number) {
        this._selectActiveView(x, y);
        this.activeView.onMousePress(x, y, buttonState);
        this.scheduleRedraw();
    }

    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number) {
        this.activeView.onMouseRelease(x, y, buttonState, prevButton);
        this.scheduleRedraw();
    }

    public onMouseIn(x: number, y: number, buttonState: number) {
        this.activeView.onMouseIn(x, y, buttonState);
        this.scheduleRedraw();
    }

    public onMouseOut(x: number, y: number, buttonState: number) {
        this.activeView.onMouseOut(x, y, buttonState);
        this.scheduleRedraw();
    }

    public onDoubleClick(x: number, y: number) {
        this._selectActiveView(x, y);
        this.activeView.onDoubleClick(x, y);
        this.scheduleRedraw();
    }

    public onMove(x: number, y: number, buttonState: number) {
        this.activeView.onMove(x, y, buttonState);
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number) {
        this.activeView.onDrag(x, y, 0, buttonState);
        this.scheduleRedraw();
    }

    public onWheel(x: number, y: number, delta: number, buttonState: number) {
        this._selectActiveView(x, y);
        this.activeView.onWheel(x, y, delta, buttonState);
        this.scheduleRedraw();
    }

    public onTouchStart(x: number, y: number, numTouches: number) {
        this._selectActiveView(x, y);
        this.activeView.onTouchStart(x, y, numTouches);
        this.scheduleRedraw();
    }

    public onTouchEnd(x: number, y: number, remainingTouch, aDraggedDuringTouch: boolean) {
        this.activeView.onTouchEnd(x, y, remainingTouch, aDraggedDuringTouch);
        this.scheduleRedraw();
    }

    public onSingleTap(x: number, y: number) {
        this.activeView.onSingleTap(x, y);
        this.scheduleRedraw();
    }

    public onDoubleTap(x: number, y: number) {
        this.activeView.onDoubleTap(x, y);
        this.scheduleRedraw();
    }

    public onTouchMove(x: number, y: number, numTouches: number, delta: number) {
        this.activeView.onTouchMove(x, y, numTouches, delta);
        this.scheduleRedraw();
    }

    public onKeyPress(key: string, x: number, y: number) {
        this.activeView.onKeyPress(key);
        this.scheduleRedraw();
    }

    public onKeyUp(key: string) {
        this.activeView.onKeyUp(key);
        this.scheduleRedraw();
    }

    public onKeyDown(key: string) {
        this.activeView.onKeyDown(key);
        this.scheduleRedraw();
    }

    public onFocus(aEvt: FocusEvent) {
        // TODO: solve the on focus for the views separately
        for (let v in this.views) {
            let view = this.views[v];
            view.onFocus(aEvt);
        }
    }

    public getCameraPosition(): vec3 {
        vec3.set(this._pgc.camPos, 0, 0, 0);

        if (this.activeView !== null) {
            vec3.copy(this._pgc.camPos, this.activeView.getCamera().getCameraPosition());
        }

        return this._pgc.camPos;
    }

    public getCameraTarget(): vec3 {
        vec3.set(this._pgc.camTarget, 0, 0, 0);

        if (this.activeView !== null) {
            vec3.copy(this._pgc.camTarget, this.activeView.getCamera().getCameraTarget());
        }

        return this._pgc.camTarget;
    }

    public getCameraFov(): number {
        if (this.activeView !== null) {
            return this.activeView.getCamera().getFieldOfView();
        }

        return 0.0;
    }

    /**
     * Take registered observer for views and inherit them between view modes. We can switch between view modes with
     * different count of views, so for simplicity now we just inherit the observers between the first view in each
     * view mode. Later we should handle somehow the case that when we switch from quad view to single view, we should
     * (or should we?) keep the registered observer or maybe reinit the observers via application module
     *
     * @param sourceViewMode A view mode instance form which we inherit the registered observers for views
     */
    public inheritObservers(sourceViewMode: ViewMode) {
        if (!sourceViewMode) {
            return;
        }

        let activeView = this.getActiveView();
        let oldActiveView = sourceViewMode.getActiveView();

        activeView.inheritObservers(oldActiveView);
    }

    private destroy() {
        this.activeView = null;
        this.views = [];

        this.rdocument.unregisterObserver(this);
    }

    private _selectActiveView(x: number, y: number) {
        for (let view of this.views) {
            if (view.isPixelInView(x, y)) {
                this.activeView = view;
                break;
            }
        }
    }

    private onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean) {
        this._selectActiveView(x, y);
        this.activeView.onClick(x, y, buttonState, aDraggedDuringClick);
        this.scheduleRedraw();
    }

    private setView(aPosition: vec3, aTarget: vec3, aFov: number) {
        for (let view of this.views) {
            view.setCameraParams(aPosition, aTarget, aFov);
        }
    }

    private setActiveView(aPosition: vec3, aTarget: vec3, aFov: number) {
        if (this.activeView) {
            this.activeView.setCameraParams(aPosition, aTarget, aFov);
        }
    }
}
